import { useEffect, useMemo, useState } from "react";
import { copy } from "../copy";
import SiteFooter from "../components/SiteFooter";
import RankTable from "../ranking/RankTable";
import { sortRows, useRankings, type RankingSort } from "../ranking/data";

const SORTS: RankingSort[] = ["won", "entered", "winnings"];

// The leaderboard. Rows, ranks, and totals come from /v1/web/rankings; the
// sort buttons only reorder what the server sent.
export default function Ranking() {
  const state = useRankings();
  const [sort, setSort] = useState<RankingSort>("won");
  const c = copy.ranking;

  useEffect(() => {
    document.title = c.title;
  }, [c.title]);

  const rows = useMemo(() => (state.status === "ready" ? sortRows(state.rows, sort) : []), [state, sort]);
  const ready = state.status === "ready" && state.rows.length > 0;

  return (
    <div className="min-h-screen overflow-x-hidden bg-ground font-sans text-chrome antialiased">
      <main className="px-4 pb-20 pt-24 md:px-6 md:pb-28 md:pt-32">
        <div className="mx-auto w-full max-w-[1240px]">
          <header className="border-b border-ground-edge pb-10 text-center">
            <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{c.eyebrow}</p>
            <h1 className="mt-3 font-headline text-5xl uppercase leading-[0.9] tracking-[-0.02em] text-chrome md:text-7xl">
              {c.heading}
            </h1>
            <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-chrome-dim">{c.sub}</p>
          </header>

          {ready && state.status === "ready" && (
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { label: c.producersRanked, value: state.stats.producers_ranked },
                { label: c.contestsEntered, value: state.stats.contests_entered },
                { label: c.contestsWon, value: state.stats.contests_won },
              ].map((stat) => (
                <div key={stat.label} data-testid="rank-stat" className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
                  <p className="bf-tabular font-headline text-4xl leading-none text-gold">{stat.value.toLocaleString("en-US")}</p>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{stat.label}</p>
                </div>
              ))}
            </div>
          )}

          <section className="mt-10" aria-label={c.caption}>
            {ready && (
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-[11px] uppercase tracking-[0.2em] text-chrome-dim">{c.sortBy}</h2>
                <div className="bf-no-scrollbar inline-flex max-w-full overflow-x-auto rounded-full border border-ground-edge bg-ground-raised p-1">
                  {SORTS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSort(key)}
                      aria-pressed={sort === key}
                      className={`bf-focusable cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
                        sort === key ? "bg-gold text-ground" : "text-chrome-dim hover:text-chrome"
                      }`}
                    >
                      {c.sorts[key]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {state.status !== "loading" && (
              <div className="rounded-2xl border border-ground-edge bg-ground-raised p-4 md:p-6">
                {state.status === "error" ? (
                  <p data-testid="ranking-error" className="py-10 text-center text-sm text-chrome-dim">
                    {c.error}
                  </p>
                ) : rows.length === 0 ? (
                  <p data-testid="ranking-empty" className="py-10 text-center text-sm text-chrome-dim">
                    {c.empty}
                  </p>
                ) : (
                  <RankTable rows={rows} />
                )}
              </div>
            )}
            {ready && <p className="mt-3 text-[11px] text-chrome-dim lg:hidden">{c.swipe}</p>}
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
